import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
  useContext,
  useMemo,
  useLayoutEffect,
} from "react";
import { useFloating, flip, shift } from "@floating-ui/react";
import { autoUpdate } from "@floating-ui/dom";
import { createPortal } from "react-dom";
import { ReactRenderer } from "@tiptap/react";
import { useTranslation } from "react-i18next";
import cx from "classnames";

import { editorContext } from "../context";

import {
  ContextualMenuProps,
  ContextMenuCommand,
  MenuItem,
} from "./ContextualMenu.decl";
import { Commands } from "./commands";
import { onMainKeysPress, setHeadingChain } from "./utils";

const ContextualMenuWithItems = ({
  virtualReference,
  items,
  editor,
  range,
}: ContextualMenuProps) => {
  const { entityId, entityType } = useContext(editorContext);
  const { t } = useTranslation("contextualMenu");
  const { x, y, strategy, refs } = useFloating({
    placement: "bottom-start",
    middleware: [shift(), flip()],
    whileElementsMounted: autoUpdate,
  });
  const menu = useRef<HTMLDivElement | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [activeItemIdx, setActiveItemIdx] = useState(0);

  const menuItems = Array.from(menu.current?.children || []);

  useLayoutEffect(() => {
    refs.setPositionReference({
      getBoundingClientRect: virtualReference.getBoundingClientRect,
    });
  }, [refs.setPositionReference, virtualReference.getBoundingClientRect]);

  useEffect(() => {
    const onKeyPress = (e: KeyboardEvent) => {
      onMainKeysPress(e, {
        ArrowUp: () => {
          setActiveItemIdx(activeItemIdx === 0 ? 0 : activeItemIdx - 1);
        },
        ArrowDown: () => {
          setActiveItemIdx(
            activeItemIdx === items.length - 1
              ? items.length - 1
              : activeItemIdx + 1
          );
        },
        Enter: () => {
          if (items[activeItemIdx].title === "image") {
            fileInput.current?.click();
          } else {
            items[activeItemIdx].command?.({ editor, range });
          }
        },
      });
    };

    document.addEventListener("keydown", onKeyPress);

    return () => document.removeEventListener("keydown", onKeyPress);
  }, [items, activeItemIdx, menuItems, editor, range]);

  const onFileInputChange = useCallback(
    async ({ target: { files } }: React.ChangeEvent<HTMLInputElement>) => {
      if (!entityId) return;

      const file = files![0];

      // TODO: UPLOAD FILE!
      // const { data } = await mutation({
      //   variables: {
      //     file,
      //     uploadableId: entityId,
      //     uploadableType: entityType,
      //   },
      // })

      if (!!file) {
        editor
          .chain()
          .focus()
          .setImage({ src: file?.name })
          .deleteRange({ from: range.from - 1, to: range.to })
          .run();
      }
      // Else: handle error?
    },
    [editor, entityId, entityType, range.from, range.to]
  );

  const groupedItems = useMemo(
    () =>
      items.reduce((acc, currentVal, idx) => {
        if (!!acc[currentVal.group]) {
          acc[currentVal.group].push({ ...currentVal, realIdx: idx });
        } else {
          acc[currentVal.group] = [{ ...currentVal, realIdx: idx }];
        }

        return acc;
      }, {} as { [key: string]: MenuItem[] }),
    [items]
  );

  const groupedItemsLength = useMemo(
    () => Object.values(groupedItems).flatMap((group) => group).length,
    [groupedItems]
  );

  useEffect(() => {
    if (groupedItemsLength > 0) {
      setActiveItemIdx(0);
    }
  }, [groupedItemsLength]);

  if (editor.isActive("bulletList") || editor.isActive("heading", { level: 1 }))
    return null;

  if (!editor) return null;

  return createPortal(
    <div
      ref={refs.setFloating}
      style={{
        position: strategy,
        top: y ?? "",
        left: x ?? "",
      }}
      className="fixed"
    >
      <input
        ref={fileInput}
        type="file"
        style={{ display: "none" }}
        onChange={onFileInputChange}
      />
      <div
        ref={menu}
        className="flex flex-col bg-white rounded z-90 shadow-lg w-64 overflow-hidden"
      >
        {Object.keys(groupedItems).map((groupKind) => (
          <Fragment key={groupKind}>
            <div
              tabIndex={-1}
              className="text-sm uppercase font-bold border-t border-gray-300 first:border-t-none"
            >
              {t(`options.${groupKind}`)}
            </div>
            {groupedItems[groupKind].map(
              ({ title, realIdx, command }, index) => (
                <button
                  key={title}
                  id={title}
                  className={cx(
                    "h-10 p-0 border-none appearance-none text-left py-0 px-3 bg-gray-300 cursor-pointer hover:bg-gray-400",
                    {
                      "bg-gray-400 hover:bg-gray-500":
                        activeItemIdx === realIdx,
                    }
                  )}
                  onMouseDown={(e) => {
                    e.preventDefault();

                    if (items[index].title === "image") {
                      fileInput.current?.click();
                    } else {
                      command?.({ editor, range });
                    }
                  }}
                >
                  {t(`options.${title}`)}
                </button>
              )
            )}
          </Fragment>
        ))}
      </div>
    </div>,
    document.getElementById("editor-area") as HTMLDivElement
  );
};

export const ContextualMenu = Commands.configure({
  suggestion: {
    items: ({ query }) =>
      (
        [
          {
            title: "heading-one",
            group: "basic-blocks",
            command: ({ editor, range }: ContextMenuCommand) =>
              setHeadingChain(editor, range, 2),
          },
          {
            title: "heading-two",
            group: "basic-blocks",
            command: ({ editor, range }: ContextMenuCommand) =>
              setHeadingChain(editor, range, 3),
          },
          {
            title: "heading-three",
            group: "basic-blocks",
            command: ({ editor, range }: ContextMenuCommand) =>
              setHeadingChain(editor, range, 4),
          },
          {
            title: "footnote",
            group: "basic-blocks",
            command: ({ editor, range: { from, to } }: ContextMenuCommand) => {
              editor
                .chain()
                .deleteRange({ from, to })
                // .setTextSelection({ from, to: to - 4 })
                .setFootnote({ footnoteNum: 1 })
                .focus()
                .run();
            },
          },
          {
            title: "divider",
            group: "basic-blocks",
            command: ({ editor, range }: ContextMenuCommand) => {
              const { from, to } = range;
              const chain = editor.chain().deleteRange({ from, to });

              chain.setHorizontalRule().run();
            },
          },
          {
            title: "image",
            group: "media-blocks",
          },
        ] as MenuItem[]
      ).filter((item) =>
        item.title.toLowerCase().startsWith(query.toLowerCase())
      ),
    render: () => {
      let component: ReactRenderer;

      return {
        onStart: (props) => {
          component = new ReactRenderer(ContextualMenuWithItems as any, {
            props: {
              ...props,
              virtualReference: { getBoundingClientRect: props.clientRect },
            },
            editor: props.editor,
          });
        },
        onUpdate(props) {
          component.updateProps({
            ...props,
            virtualReference: { getBoundingClientRect: props.clientRect },
          });
        },
        onKeyDown: ({ event }) =>
          event.key === "Enter" ||
          event.key === "ArrowDown" ||
          event.key === "ArrowUp",
        onExit() {
          component.destroy();
        },
      };
    },
  },
});
